import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import {
  Archive,
  ArchiveRestore,
  ArrowUpRight,
  EyeOff,
  Loader2,
  MessageCircle,
  MessageSquareText,
  Pin,
} from 'lucide-react'
import * as Sentry from '@sentry/electron/renderer'
import { AnimatePresence, motion, useReducedMotion, type Transition, type Variants } from 'framer-motion'
import { TodoMark } from '@highlight/ui'
import { twMerge } from 'tailwind-merge'
import { askUserCardVariant, isAnswerableStagedAction } from '@renderer/chat/integrations/answerable'
import { AskUserBadge } from '@renderer/chat/integrations/ask-user-card'
import { isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user'
import { ActionIconSlot } from '@renderer/components/action-icon-slot'
import { InvokeIcon } from '@renderer/components/invoke-icon'
import { StreamingChatIndicator } from '@renderer/components/streaming-chat-indicator'
import { getActionIcon, getActionTitle } from '@renderer/overlay/Sidepanel/utils/activity-helpers'

import { useForYouChatsFeed } from '@renderer/overlay/hooks/use-running-chats'
import {
  deriveFlowAnimationState,
  FROM_A_CHAT_LABEL,
  getForYouChatSubtitles,
  groupForYouChats,
  isRegularChat,
  selectForYouChatPreview,
  selectOutstandingActions,
  type SidepanelChat,
} from '@renderer/overlay/hooks/running-chat-selection'
import { useClearForYouChat, useRestoreForYouChat } from '@renderer/overlay/hooks/use-clear-for-you-chat'
import {
  useForYouDisplayChats,
  useForYouExecutedLinger,
  useForYouLingerControls,
  useForYouLingerEntry,
} from '@renderer/overlay/hooks/for-you-linger'
import type { ForYouLingerEntry } from '@renderer/overlay/hooks/for-you-linger-state'
import {
  COMPLETED_ACTION_DURATION_MS,
  useSetStagedActionsPinned,
  useStagedActionStatus,
  useVisibleStagedActions,
  type StagedAction,
} from '@renderer/overlay/Sidepanel/hooks/use-staged-actions'
import { isPinnedChat } from './todos-order'
import { useStagedActionCallbacks } from '@renderer/overlay/Sidepanel/hooks/use-staged-action-view'
import { ActionStatusRing } from '@renderer/overlay/Sidepanel/components/action-status-ring'
import {
  CellActionMenuItem,
  CellActionsCluster,
  CellActionsMenu,
  CellContextMenu,
  undoCellAction,
  type CellAction,
} from '@renderer/overlay/Sidepanel/components/cell-actions'
import { CellHoverButtons } from '@renderer/overlay/Sidepanel/components/cell-hover-buttons'
import { CellPreviewCard } from '@renderer/overlay/Sidepanel/components/cell-preview-card'
import { ConversationSkillIcon } from '@renderer/overlay/Sidepanel/components/conversation-skill-icon'
import { SidepanelCellRow } from '@renderer/overlay/Sidepanel/components/cells/row'
import {
  SIDEPANEL_CELL_SURFACE,
  SIDEPANEL_FILLED_HOVER_SURFACE,
  TODO_MARK,
} from '@renderer/overlay/Sidepanel/components/cells/surface'
import { TodoTag, TodoTags } from '@renderer/overlay/Sidepanel/components/cells/todo-tags'
import { TODO_VIEW_TITLE_ROW, TodoViewTitle } from '@renderer/overlay/Sidepanel/components/cells/todo-view-title'
import {
  givesWayToTodoActions,
  TodoActionButton,
  TodoActions,
} from '@renderer/overlay/Sidepanel/components/cells/todo-actions'
import { UnreadBadge } from '@renderer/overlay/Sidepanel/components/unread-badge'
import { useForYouUnreadCount } from '@renderer/overlay/hooks/use-for-you-unread-count'
import { useViewActions, useViewOpenHandlers, type ViewOpenTarget } from '../../../view-atoms'
import { chatOutputContextOf } from '../../../views/chat-output-view/use-chat-output-view-model'
import { useIsSidepanelSectionCollapsed } from '../../../atoms'
import { ForYouChatPreview } from './for-you-chat-preview'
import { isDailyBriefChat } from '../../day-zero-tour/live-stop'
import { useActiveTourTarget } from '../../day-zero-tour/tour-state'
import { SidepanelSectionViewMore } from '../nested/section-view-more'
import { absorbRemainingItems, useProgressiveSectionLimit } from '../use-progressive-section-limit'
import { useIsActionHeld } from '@renderer/overlay/Sidepanel/views/action-view/use-action-lock'
import { useHasTodosCountdown } from '../../../hooks/reminder-linger'

const FOR_YOU_INITIAL_LIMIT = 5

const ROW_TRANSITION: Transition = {
  duration: 0.24,
  ease: [0.22, 1, 0.36, 1],
}

const NO_MOTION: Transition = { duration: 0 }

// A leaving row collapses in flow — no transforms on its neighbours — so the gap
// closes as it goes. The negative margin cancels the list's flex gap at height 0.
const ROW_VARIANTS: Variants = {
  initial: { opacity: 0, y: -10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, height: 0, marginBottom: -2, overflow: 'hidden' },
}

// When the empty item replaces a leaving row it grows on the same clock the row
// shrinks, so what sits below the list holds still instead of dropping and
// riding back up. Arriving on its own, it mounts at full size. It sits outside
// the presence tree so it leaves in the same render a new row arrives in —
// an exit would stack its height on top of the incoming row's.
const EMPTY_VARIANTS: Variants = {
  initial: { opacity: 0, height: 0, overflow: 'hidden' },
  animate: { opacity: 1, height: 'auto', overflow: 'visible' },
}

export const SidepanelRunningChatsSection = memo(function SidepanelRunningChatsSection() {
  const {
    data: chats,
    isLoading,
    isError,
    feedKey,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useForYouChatsFeed()
  const displayChats = useForYouDisplayChats(chats, feedKey)
  const isCollapsed = useIsSidepanelSectionCollapsed('actions')
  const { visibleCount, nextVisibleCount, showMore } = useProgressiveSectionLimit(
    'actions',
    FOR_YOU_INITIAL_LIMIT,
    isCollapsed,
  )

  if (isLoading) {
    return <p className="text-foreground-secondary type-ui-sm px-3 py-1">Loading chats…</p>
  }

  if (isError) {
    return <p className="text-danger-subtle type-ui-sm px-3 py-1">Couldn’t load chats. Try refreshing.</p>
  }

  const visibleChatCount = hasNextPage ? visibleCount : absorbRemainingItems(visibleCount, displayChats.length)
  const preview = selectForYouChatPreview(displayChats, visibleChatCount)
  const canShowMore = preview.hasMore || hasNextPage

  const handleViewMore = () => {
    showMore()
    if (nextVisibleCount >= displayChats.length && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage().catch((error) => Sentry.captureException(error))
    }
  }

  return (
    <>
      <ForYouChatsList
        chats={preview.chats}
        sourceLabel="Home"
        feedKey={feedKey}
        empty={<p className="text-foreground-secondary type-ui-sm px-3 py-1">Nothing for you yet.</p>}
      />
      {canShowMore && <SidepanelSectionViewMore onClick={handleViewMore} disabled={isFetchingNextPage} />}
    </>
  )
})

export const ForYouUnreadBadge = memo(function ForYouUnreadBadge() {
  const count = useForYouUnreadCount()
  return <UnreadBadge count={count} />
})

interface ForYouChatsListProps {
  /** Already resolved against linger state by the feed owner — see `useForYouDisplayChats`. */
  chats: Array<SidepanelChat>
  sourceLabel: string
  /** The feed these rows came from, so a row archived here is held here. */
  feedKey: string
  afterInProgress?: ReactNode
  /**
   * Shown once the list is empty. Rendered here rather than in place of the list so
   * the last row still gets its exit — swapping the list out would drop it mid-hold.
   */
  empty?: ReactNode
  /** As on `ForYouChatRow`. */
  appearance?: 'card' | 'todo'
}

export const ForYouChatsList = memo(function ForYouChatsList({
  chats,
  sourceLabel,
  feedKey,
  afterInProgress,
  empty,
  appearance,
}: ForYouChatsListProps) {
  // Use the staged-action cache so titles and integration icons match the output view.
  const { allActions } = useVisibleStagedActions()
  const actions = useMemo(() => new Map(allActions.map((action) => [action.id, action])), [allActions])
  const { previewlessStreaming, visibleStreaming, actionable } = groupForYouChats(chats)
  const completedFlowIds = useCompletedFlowIds(chats)
  const shouldReduceMotion = useReducedMotion()
  useForYouExecutedLinger(chats, feedKey, actions)
  const emptyReplacesRow = useEmptyReplacesRow(chats.length)
  const touring = useActiveTourTarget() !== null

  const renderChat = (chat: SidepanelChat) => (
    <motion.div
      key={chat.id}
      variants={ROW_VARIANTS}
      initial={completedFlowIds.has(chat.id) ? 'initial' : false}
      animate="animate"
      exit="exit"
      transition={shouldReduceMotion ? NO_MOTION : ROW_TRANSITION}
      data-tour-section={touring && isDailyBriefChat(chat, actions) ? 'daily-brief-card' : undefined}
    >
      <ForYouChatRow
        chat={chat}
        actions={actions}
        sourceLabel={sourceLabel}
        feedKey={feedKey}
        position={chats.indexOf(chat)}
        appearance={appearance}
      />
    </motion.div>
  )

  // One presence tree for every row, so a chat that moves between the streaming
  // and actionable groups is the same child to it and never exits mid-move.
  return (
    <div className="flex flex-col gap-[2px] empty:hidden">
      <AnimatePresence initial={false}>
        {previewlessStreaming.length > 0 && <ChatsInProgress key="in-progress" chats={previewlessStreaming} />}
        {visibleStreaming.map(renderChat)}
        {afterInProgress != null && <Fragment key="after-in-progress">{afterInProgress}</Fragment>}
        {actionable.map(renderChat)}
      </AnimatePresence>
      {chats.length === 0 && empty != null && (
        <motion.div
          variants={EMPTY_VARIANTS}
          initial={emptyReplacesRow ? 'initial' : false}
          animate="animate"
          transition={shouldReduceMotion ? NO_MOTION : ROW_TRANSITION}
        >
          {empty}
        </motion.div>
      )}
    </div>
  )
})

/** True on the render where the list just went from rows to none, i.e. a row is exiting. */
function useEmptyReplacesRow(chatCount: number): boolean {
  const previousCountRef = useRef(chatCount)
  const replacesRow = chatCount === 0 && previousCountRef.current > 0

  useEffect(() => {
    previousCountRef.current = chatCount
  }, [chatCount])

  return replacesRow
}

function useCompletedFlowIds(chats: Array<SidepanelChat>): ReadonlySet<string> {
  const pendingFlowIdsRef = useRef(new Set<string>())
  const { completedFlowIds, pendingFlowIds } = deriveFlowAnimationState(chats, pendingFlowIdsRef.current)

  useEffect(() => {
    pendingFlowIdsRef.current = pendingFlowIds
  }, [pendingFlowIds])

  return completedFlowIds
}

export function ChatsInProgress({ chats, className }: { chats: Array<SidepanelChat>; className?: string }) {
  const count = chats.length
  const label = `${count} ${count === 1 ? 'task' : 'tasks'} in progress`

  return (
    <CellPreviewCard
      triggerClassName="block w-full"
      popupClassName="w-64 max-w-64 gap-0.5 p-1.5"
      openDelay={0}
      preview={
        <div className="flex flex-col gap-0.5">
          {chats.map((chat) => (
            <div key={chat.id} className="flex w-full items-center gap-2.5 px-1.5 py-1.5">
              {isRegularChat(chat) ? (
                <ActionIconSlot className="self-center">
                  <MessageCircle />
                </ActionIconSlot>
              ) : chat.skillId ? (
                <ConversationSkillIcon skillId={chat.skillId} className="self-center" />
              ) : (
                <ActionIconSlot className="self-center">
                  <MessageSquareText />
                </ActionIconSlot>
              )}
              <span className="text-foreground type-ui-sm min-w-0 flex-1 truncate font-normal">
                {chat.title ?? 'Untitled chat'}
              </span>
            </div>
          ))}
        </div>
      }
    >
      <button
        type="button"
        aria-label={`Show ${label}`}
        className={twMerge(
          'text-orange-11 type-ui-sm flex w-full items-center justify-start gap-2.5 px-3 py-2',
          SIDEPANEL_CELL_SURFACE,
          className,
        )}
      >
        <StreamingChatIndicator label={label} size={16} tone="dark-orange" />
        <span>{label}</span>
      </button>
    </CellPreviewCard>
  )
}

export const ForYouChatRow = memo(function ForYouChatRow({
  chat,
  actions,
  sourceLabel,
  feedKey,
  position,
  archived = false,
  appearance = 'card',
  marked = true,
}: {
  chat: SidepanelChat
  actions: ReadonlyMap<string, StagedAction>
  sourceLabel: string
  feedKey: string
  position: number
  archived?: boolean
  /**
   * `todo` draws the row as a half-done to-do for the To-dos section. A to-do
   * mark leads instead of the integration badge, and tags replace the subtitle.
   * Ticking the mark archives the row, and unticking it during the linger
   * restores it.
   */
  appearance?: 'card' | 'todo'
  /** `false` drops a `todo` row's mark, for a row nested under a to-do whose mark ticks for it. */
  marked?: boolean
}) {
  const { openView } = useViewActions()
  const { clearForYouChat, isClearing } = useClearForYouChat()
  const { restoreForYouChat, isRestoring } = useRestoreForYouChat()
  const { undoStagedAction } = useStagedActionCallbacks()
  const { start: startLinger, cancel: cancelLinger } = useForYouLingerControls()
  const lingerEntry = useForYouLingerEntry(chat.id)
  const linger = archived ? undefined : lingerEntry
  const [ctxOpen, setCtxOpen] = useState(false)
  const hasTodosCountdown = useHasTodosCountdown()
  const pinned = isPinnedChat(chat, actions)
  const setPinned = useSetStagedActionsPinned()
  const openDestinationWith = useCallback(
    (open: ViewOpenTarget) => {
      const options = { sourceLabel, ...open }
      if (isRegularChat(chat)) {
        openView('chat', { chatId: chat.id, title: chat.title, instantScrollToBottom: true }, options)
        return
      }
      openView('chatOutput', chatOutputContextOf(chat), options)
    },
    [
      openView,
      chat.id,
      chat.title,
      chat.conversationType,
      chat.skillId,
      chat.preview,
      chat.stagedActionIds,
      chat.reminderIds,
      chat.audioNoteId,
      chat.previewRevision,
      sourceLabel,
    ],
  )
  const { onOpen: openDestination, onPopout: openDestinationInWindow } = useViewOpenHandlers(openDestinationWith)
  const outstandingActions = useMemo(
    () => selectOutstandingActions(chat.stagedActionIds, actions),
    [actions, chat.stagedActionIds],
  )
  const singleInputAction = outstandingActions.length === 1 ? outstandingActions[0] : undefined
  const lingerAction = linger?.actionId ? actions.get(linger.actionId) : undefined
  const displayAction = singleInputAction ?? lingerAction
  const needsInput = outstandingActions.some((action) => isAnswerableStagedAction(action))
  const needsAttention = outstandingActions.length > 0
  // A report_to_user action is a message to read, with nothing to send.
  const isReport = needsAttention && outstandingActions.every(isReportToUserStagedAction)
  // A to-do draft names the app it'll send through by its icon, as its last tag.
  const draftIcon = appearance === 'todo' && displayAction && !isReport ? getActionIcon(displayAction, 12) : undefined
  const subtitles = archived
    ? ['Archived']
    : getForYouChatSubtitles(chat, outstandingActions.length, needsInput, appearance)
  const archiveLabel = outstandingActions.length > 1 ? `Archive ${outstandingActions.length} actions` : 'Archive action'
  const iconIndicator = archived ? undefined : needsInput ? 'attention' : chat.isUnread ? 'unread' : undefined
  const clearChat = useCallback(() => {
    // A to-do's archive waits for To-dos' shared countdown, so taking it back never reaches the server.
    // Where no countdown is on screen to send it, as in the For You view, it's sent at once.
    const waitsForCountdown = appearance === 'todo' && hasTodosCountdown
    startLinger({
      chat,
      kind: 'archived',
      feedKey,
      position,
      startedAt: Date.now(),
      sharesTodosCountdown: waitsForCountdown,
    })
    if (waitsForCountdown) return
    void clearForYouChat(chat.id).catch((error) => {
      cancelLinger(chat.id)
      Sentry.captureException(error)
    })
  }, [appearance, cancelLinger, chat, clearForYouChat, feedKey, hasTodosCountdown, position, startLinger])
  const undoLinger = useCallback(() => {
    if (linger?.sharesTodosCountdown) {
      cancelLinger(chat.id)
      return
    }
    const undo =
      linger?.kind === 'executed' && linger.actionId
        ? undoStagedAction(linger.actionId).then(() => undefined)
        : restoreForYouChat(chat.id)
    void undo.then(() => cancelLinger(chat.id)).catch((error) => Sentry.captureException(error))
  }, [cancelLinger, chat.id, linger, restoreForYouChat, undoStagedAction])

  const openOnEnterOrSpace = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget || (event.key !== 'Enter' && event.key !== ' ')) return
      event.preventDefault()
      openDestination(event)
    },
    [openDestination],
  )

  const lingerLabel = linger?.kind === 'archived' ? 'Archived' : 'Completed'
  // A notification's heading already says what its labels would.
  const todoLabels = linger ? [lingerLabel] : isReport ? [] : subtitles
  const canUndoLinger = linger?.kind === 'archived' || lingerAction?.view?.supportsUndo === true
  const title = isRegularChat(chat)
    ? (chat.title ?? 'Untitled chat')
    : displayAction
      ? getActionTitle(displayAction)
      : (chat.title ?? 'Untitled chat')

  // The one input action a row can send straight from its own controls. Anything
  // that needs an answer is opened instead, so it keeps its framing, and so is a
  // locked preview, whose upgrade lives in the Action View.
  const isLocked = useIsActionHeld(singleInputAction)
  const sendableAction =
    !archived &&
    !linger &&
    !isLocked &&
    !isRegularChat(chat) &&
    singleInputAction &&
    !isAnswerableStagedAction(singleInputAction) &&
    !isReportToUserStagedAction(singleInputAction)
      ? singleInputAction
      : undefined

  const rowActions: Array<CellAction> = archived
    ? [
        {
          key: 'restore',
          label: 'Restore to For You',
          icon: <ArchiveRestore />,
          disabled: isRestoring,
          onSelect: undoLinger,
        },
      ]
    : linger
      ? canUndoLinger
        ? [undoCellAction(undoLinger, { disabled: isRestoring || isClearing })]
        : []
      : [
          isRegularChat(chat)
            ? {
                key: 'dismiss',
                label: 'Dismiss',
                icon: <EyeOff />,
                disabled: isClearing,
                onSelect: clearChat,
              }
            : {
                key: 'archive',
                label: archiveLabel,
                icon: <Archive />,
                disabled: isClearing,
                onSelect: clearChat,
              },
          ...(isRegularChat(chat)
            ? [
                {
                  key: 'open-chat',
                  label: 'Open chat',
                  icon: <MessageCircle />,
                  tone: 'brand',
                  onSelect: openDestination,
                } satisfies CellAction,
              ]
            : !sendableAction && needsAttention && !needsInput
              ? [
                  {
                    key: 'open-output',
                    label: 'Review',
                    icon: <ArrowUpRight />,
                    tone: 'brand',
                    onSelect: openDestination,
                  } satisfies CellAction,
                ]
              : []),
        ]

  // Not during a linger. The countdown owns the row's controls, and the row is
  // leaving the list anyway.
  const popout = linger ? undefined : openDestinationInWindow

  return (
    <div className="flex flex-col">
      <CellContextMenu
        onOpenChange={setCtxOpen}
        items={
          <CellActionsMenu actions={rowActions} onPopout={popout}>
            {sendableAction && <SendActionMenuItem action={sendableAction} />}
          </CellActionsMenu>
        }
      >
        <CellPreviewCard
          // No `group/action` on a to-do, which has no cluster, so only its own hover lights it.
          triggerClassName={twMerge('group/cell relative flex flex-col', appearance !== 'todo' && 'group/action')}
          // No card over an open context menu, where it would sit on top of the menu, nor on a to-do,
          // which opens in the panel in its place.
          disabled={appearance === 'todo' || ctxOpen || !!linger || (!chat.preview && outstandingActions.length === 0)}
          preview={
            <ForYouChatPreview
              chat={chat}
              actions={outstandingActions}
              sourceLabel={sourceLabel}
              onOpen={openDestination}
            />
          }
        >
          {appearance === 'todo' ? (
            <SidepanelCellRow
              className={`py-1.5 ${SIDEPANEL_FILLED_HOVER_SURFACE}`}
              icon={
                marked && (
                  <TodoMark
                    progress={isReport ? 'ready' : 'drafted'}
                    checked={!!linger}
                    disabled={!!linger && !canUndoLinger}
                    onChange={linger ? undoLinger : clearChat}
                    // The row opens on Enter/Space, so the mark keeps its own.
                    onKeyDown={(event) => event.stopPropagation()}
                    aria-label={linger ? `Undo: ${title}` : `Done: ${title}`}
                    className={`mt-[3px] ${TODO_MARK}`}
                  />
                )
              }
              title={
                <TodoViewTitle
                  className={linger ? 'decoration-foreground-placeholder line-through' : undefined}
                  onOpen={openDestination}
                >
                  {title}
                </TodoViewTitle>
              }
              titleClassName={twMerge(
                TODO_VIEW_TITLE_ROW,
                iconIndicator && 'font-medium',
                linger && 'decoration-foreground-placeholder line-through',
              )}
              // A reminder row draws no chevron, so the tags of both kinds end at the same edge.
              showChevron={false}
              trailing={
                <>
                  {(todoLabels.length > 0 || draftIcon) && (
                    <span className={givesWayToTodoActions(!linger)}>
                      <TodoTags>
                        {todoLabels.map((label) => (
                          <TodoTag
                            key={label}
                            icon={
                              label === FROM_A_CHAT_LABEL ? (
                                <MessageCircle strokeWidth={2} className="size-2.5" />
                              ) : undefined
                            }
                          >
                            {label}
                          </TodoTag>
                        ))}
                        {draftIcon && <TodoTag icon={draftIcon} />}
                      </TodoTags>
                    </span>
                  )}
                  {!linger && (
                    <TodoActions>
                      {/* A chat pins through its staged actions, so one with none has no pin. */}
                      {chat.stagedActionIds.length > 0 && (
                        <TodoActionButton
                          label={pinned ? 'Unpin' : 'Pin'}
                          aria-pressed={pinned}
                          onClick={() => setPinned(chat.stagedActionIds, !pinned)}
                        >
                          <Pin className={pinned ? 'fill-current' : undefined} />
                        </TodoActionButton>
                      )}
                      {/* One Archive for chats and actions alike: both only leave For You until new activity. */}
                      <TodoActionButton label="Archive" disabled={isClearing} onClick={clearChat}>
                        <Archive />
                      </TodoActionButton>
                    </TodoActions>
                  )}
                  {chat.isStreaming && <ChatStreamingIndicator title={title} />}
                </>
              }
              // A to-do opens from its title alone, or from Enter/Space while the row has focus.
              onKeyDown={openOnEnterOrSpace}
            />
          ) : (
            <>
              <SidepanelCellRow
                icon={
                  <ChatRowIcon
                    chat={chat}
                    displayAction={displayAction}
                    linger={linger}
                    iconIndicator={iconIndicator}
                  />
                }
                title={title}
                titleClassName={linger ? 'decoration-foreground-placeholder line-through' : undefined}
                subtitle={
                  subtitles.length > 0 ? (
                    <span className="text-foreground-secondary type-ui-xs truncate">
                      {linger ? lingerLabel : subtitles.join(' • ')}
                    </span>
                  ) : undefined
                }
                trailing={chat.isStreaming ? <ChatStreamingIndicator title={title} /> : undefined}
                onClick={openDestination}
              />
              <CellActionsCluster actions={rowActions} onPopout={popout}>
                {sendableAction && <SendActionButton key="send" action={sendableAction} />}
              </CellActionsCluster>
            </>
          )}
        </CellPreviewCard>
      </CellContextMenu>
    </div>
  )
})

function ChatStreamingIndicator({ title }: { title: string }) {
  return (
    <span aria-hidden="true" className="flex h-[20px] shrink-0 items-center">
      <StreamingChatIndicator label={`${title} is working`} size={16} />
    </span>
  )
}

function ChatRowIcon({
  chat,
  displayAction,
  linger,
  iconIndicator,
}: {
  chat: SidepanelChat
  displayAction: StagedAction | undefined
  linger: ForYouLingerEntry | undefined
  iconIndicator: 'attention' | 'unread' | undefined
}) {
  const overlay = linger ? (
    <ActionStatusRing
      tone={linger.kind === 'archived' ? 'neutral' : 'success'}
      expiry={{ duration: COMPLETED_ACTION_DURATION_MS, startTime: linger.startedAt }}
    />
  ) : undefined

  if (isRegularChat(chat)) {
    return (
      <ActionIconSlot overlay={overlay} indicator={iconIndicator}>
        <MessageCircle />
      </ActionIconSlot>
    )
  }
  if (displayAction && isAnswerableStagedAction(displayAction)) {
    return <AskUserBadge variant={askUserCardVariant(displayAction)} overlay={overlay} indicator={iconIndicator} />
  }
  if (displayAction) {
    return (
      <ActionIconSlot view={displayAction.view} overlay={overlay} indicator={iconIndicator}>
        {getActionIcon(displayAction)}
      </ActionIconSlot>
    )
  }
  if (chat.skillId) {
    return <ConversationSkillIcon skillId={chat.skillId} overlay={overlay} indicator={iconIndicator} />
  }
  return (
    <ActionIconSlot overlay={overlay} indicator={iconIndicator}>
      <MessageSquareText />
    </ActionIconSlot>
  )
}

/**
 * Sending the row's one outstanding action. Not a plain `CellAction` on the row
 * like its neighbours: the label and the in-flight state come from the action's
 * own status subscription, which only exists when there *is* one to send.
 */
function useSendAction(action: StagedAction): CellAction {
  const { invokeStagedAction } = useStagedActionCallbacks()
  const { isInvoking } = useStagedActionStatus(action)
  const sendAction = useCallback(() => {
    void invokeStagedAction(action.id).catch((error) => Sentry.captureException(error))
  }, [action.id, invokeStagedAction])

  return {
    key: 'send',
    label: action.view?.invokeLabel ?? 'Execute',
    icon: isInvoking ? <Loader2 className="size-4 animate-spin" /> : <InvokeIcon icon={action.view?.invokeIcon} />,
    tone: 'brand',
    disabled: isInvoking,
    onSelect: sendAction,
  }
}

function SendActionButton({ action }: { action: StagedAction }) {
  const send = useSendAction(action)

  return (
    <CellHoverButtons.Primary label={send.label} onClick={send.onSelect} disabled={send.disabled}>
      {send.icon}
    </CellHoverButtons.Primary>
  )
}

function SendActionMenuItem({ action }: { action: StagedAction }) {
  const send = useSendAction(action)

  return <CellActionMenuItem action={send} />
}
